import { useCallback, useEffect, useRef } from "react";
import type { ChatMessage } from "../types/chatMessageTypes";

// Type for the data returned by useChatMessagesInfinite
export interface ChatMessagesData {
  pages: Array<{
    messages: ChatMessage[];
    hasMore: boolean;
    beforeCreated: string;
  }>;
  messages: ChatMessage[];
}

/**
 * Custom hook for managing chat scroll behavior
 * Handles scrolling to bottom on initial load and maintaining scroll position when loading older messages
 * Uses a flipped container approach where the newest messages appear at the top
 *
 * @param data - The chat messages data
 * @param status - The status of the query (loading, success, error)
 * @returns Object containing scroll-related refs and functions
 */
export const useChatScroll = (
  data: ChatMessagesData | undefined,
  status: string,
) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isInitialLoadRef = useRef(true);
  const prevScrollHeightRef = useRef(0);

  // Function to scroll to top (newest messages in flipped container)
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      // In flipped mode, scroll to top to see newest messages
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, []);

  // Function to store current scroll height
  const storeScrollHeight = useCallback(() => {
    if (scrollContainerRef.current) {
      prevScrollHeightRef.current = scrollContainerRef.current.scrollHeight;
      return true;
    }
    return false;
  }, []);

  // Function to maintain scroll position after new content is loaded
  const maintainScrollPosition = useCallback(() => {
    if (!scrollContainerRef.current || prevScrollHeightRef.current === 0)
      return;

    prevScrollHeightRef.current = 0; // Reset for the next load trigger
  }, []);

  // Scroll to top (newest messages) on initial load
  useEffect(() => {
    if (status === "success" && isInitialLoadRef.current) {
      scrollToBottom();
      isInitialLoadRef.current = false;
    }
  }, [status, scrollToBottom]);

  // Maintain scroll position when new messages are loaded
  useEffect(() => {
    if (!data || isInitialLoadRef.current) return;
    maintainScrollPosition();
  }, [data, maintainScrollPosition]);

  return {
    scrollContainerRef,
    storeScrollHeight,
    scrollToBottom,
    isInitialLoad: isInitialLoadRef.current,
  };
};
